/**
 * ContainerConfigPanel Component for UI-Model-1
 *
 * Full configuration panel for containers with editable fields:
 * - Name (TextField, editable)
 * - Width (Input + Slider + Lock toggle)
 * - Height (display "Dynamic")
 * - Alignment (DeltaSwitch segmented control)
 * - Layout (Dropdown)
 * - Show Title (Checkbox)
 * - Collapsible (Checkbox)
 * - Behave as Filter (Checkbox)
 * - Submodel info section
 */

import React, { useCallback, useState } from 'react';
import { ContainerAPI } from '../../types/state.types';
import { useContainerOperations } from '../../hooks/useContainerOperations';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';
import { useModule } from '../../contexts/ModuleContext';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { getContainerDirectLock } from '../../container-management/validators/submodelLockValidator';
import { isFreestyle } from '../../selectors';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Divider from 'Components/DesignSystem/Divider/Divider';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Slider from 'Components/DesignSystem/Slider/Slider';
import DeltaSwitch from 'Components/GMS/Products/ProductView/DeltaSwitch';
import { LockedIcon, UnlockedIcon, ResetIcon, AllSidesIcon, TopBorderIcon, RightBorderIcon, BottomBorderIcon, LeftBorderIcon } from 'assests/GmsIcons';
import { ContainerContentsList } from './ContainerContentsList';

interface ContainerConfigPanelProps {
  container: ContainerAPI;
}

// Layout options for dropdown
const LAYOUT_OPTIONS = [
  { value: 'vertical', label: 'Vertical' },
  { value: 'horizontal', label: 'Horizontal' },
];

// ============================================================================
// Border Placement Selector
// ============================================================================

const SIDE_PLACEMENTS = [
  { value: 'top', label: 'Top', Icon: TopBorderIcon },
  { value: 'right', label: 'Right', Icon: RightBorderIcon },
  { value: 'bottom', label: 'Bottom', Icon: BottomBorderIcon},
  { value: 'left', label: 'Left', Icon: LeftBorderIcon},
] as const;

interface BorderPlacementSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
}

const BorderPlacementSelector = ({ value, onChange }: BorderPlacementSelectorProps) => {
  const isAllSelected = value.includes('all');

  const handleAllToggle = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(['all']);
    }
  };

  const handleSideToggle = (side: string) => {
    // If "all" is currently selected, clear it and start fresh with this side
    if (isAllSelected) {
      onChange([side]);
      return;
    }

    const currentSides = value.filter((v) => v !== 'all');
    if (currentSides.includes(side)) {
      // Deselect
      onChange(currentSides.filter((v) => v !== side));
    } else {
      // Select
      onChange([...currentSides, side]);
    }
  };

  return (
    <div className="tw-ml-8 tw-space-y-2">
      <FieldTitle label="Set Border" size="sm" />
      <div className="tw-flex tw-items-center tw-gap-1">
        {/* All Sides - radio behavior */}
        <button data-testid="configpanel-containerconfigpanel-button-1"
          type="button"
          onClick={handleAllToggle}
          className={`tw-p-2 tw-rounded tw-border tw-transition-colors ${
            isAllSelected
              ? 'tw-border-accent tw-bg-accent-tint tw-text-accent'
              : 'tw-border-edge tw-bg-surface tw-text-ink-secondary hover:tw-border-edge-strong'
          }`}
          title="All Sides"
        >
          <span className="icon-follow-ink"><AllSidesIcon /></span>
        </button>

        {/* OR separator */}
        <span className="tw-text-xs tw-text-ink-secondary tw-px-1">or</span>

        {/* Individual sides - multi-select toggle */}
        {SIDE_PLACEMENTS.map(({ value: side, label, Icon }) => {
          const isActive = !isAllSelected && value.includes(side);
          return (
            <button data-testid="configpanel-containerconfigpanel-button-2"
              key={side}
              type="button"
              onClick={() => handleSideToggle(side)}
              className={`tw-p-2 tw-rounded tw-border tw-transition-colors ${
                isActive
                  ? 'tw-border-accent tw-bg-accent-tint tw-text-accent'
                  : 'tw-border-edge tw-bg-surface tw-text-ink-secondary hover:tw-border-edge-strong'
              }`}
              title={label}
            >
              <Icon />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const ContainerConfigPanel = ({
  container,
}: ContainerConfigPanelProps) => {
  const { updateContainer } = useContainerOperations();
  const { getSubmodelInstanceLimits, getSubmodelName } = useModule();
  const { state } = useUIModelState();

  // Submodel lock indicator (read-only), derived SOLELY from the container's OWN direct
  // field/category/connector items (Rule 1/2) — NOT the cached `content.submodel` (which
  // can be stale or propagated from an ancestor), and NOT an ancestor's max>1 cascade.
  // A container with no direct submodel-bound item (widget-only, nested-container-only, or
  // empty) shows no lock, even inside a max>1 group — the badge advertises a container's own
  // content, not what enforcement would block on it. Enforcement (Rule 4 cascade) is unchanged.
  // Free style: containers never lock, so no indicator at all.
  const lockedSubmodelId = isFreestyle(state.model) ? null : getContainerDirectLock(container);
  const boundSubmodel = container.content?.submodel;
  const lockedSubmodelName = lockedSubmodelId
    ? getSubmodelName(lockedSubmodelId) ??
      (boundSubmodel && boundSubmodel.id === lockedSubmodelId && 'name' in boundSubmodel
        ? boundSubmodel.name
        : lockedSubmodelId)
    : '';
  const lockedSubmodelMax = lockedSubmodelId
    ? getSubmodelInstanceLimits(lockedSubmodelId).maxInstantiation
    : 1;

  // Width management hook
  const {
    localWidth,
    isLocked,
    maxWidth,
    minWidth,
    containersInRow,
    handleWidthChange,
    toggleLock,
    resetRowWidths,
  } = useContainerWidth(container.id);

  // Debounced name input
  const nameInput = useDebouncedInput(
    container.name || '',
    (value) => {
      updateContainer(container.id, { name: value });
    },
    500
  );

  // Handle configuration updates
  const handleConfigUpdate = useCallback(
    (key: string, value: unknown) => {
      updateContainer(container.id, {
        configuration: {
          ...container.configuration,
          [key]: value,
        },
      });
    },
    [container.id, container.configuration, updateContainer]
  );

  // Handle width input change
  const handleWidthInputChange = useCallback(
    (value: string) => {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        handleWidthChange(Math.max(minWidth, Math.min(maxWidth, numValue)));
      }
    },
    [handleWidthChange, minWidth, maxWidth]
  );

  // Handle slider change
  /* `Slider` hands back the value, so the `parseInt(e.target.value, 10)` every
     call site used to repeat is gone. */
  const handleSliderChange = useCallback(
    (value: number) => {
      handleWidthChange(value);
    },
    [handleWidthChange]
  );

  return (
    <div className="tw-space-y-4">
      {/* Header */}
      <Typography variant="title" className="tw-text-ink">
        Container Configuration
      </Typography>
      <Divider />

      {/* Name Field */}
      <TextField
        label="Name"
        labelSize="sm"
        value={nameInput.localValue}
        onChange={nameInput.handleChange}
        placeholder="Enter container name"
      />

      {/* Width Control Section */}
      <div className="tw-space-y-2">
        <FieldTitle label="Width" size="sm" />

        {/* Width Input with Lock */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-relative tw-flex-1">
            <input data-testid="configpanel-containerconfigpanel-input-3"
              type="text"
              inputMode="numeric"
              value={Math.round(localWidth)}
              onChange={(e) => handleWidthInputChange(e.target.value)}
              disabled={isLocked}
              className={`tw-w-full tw-px-3 tw-py-2 tw-pr-8 tw-text-sm tw-border tw-rounded-sm tw-transition-colors
                ${isLocked
                  ? 'tw-bg-surface-sunken tw-border-edge tw-text-ink-disabled tw-cursor-not-allowed'
                  : 'tw-bg-surface tw-border-edge tw-text-ink focus:tw-border-accent focus:tw-outline-none'
                }`}
            />
            <span className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-sm tw-text-ink-secondary">
              %
            </span>
          </div>

          {/* Lock Toggle Button */}
          <button data-testid="configpanel-containerconfigpanel-button-4"
            onClick={toggleLock}
            className={`tw-p-2 tw-rounded tw-transition-colors tw-flex-shrink-0 ${
              isLocked
                ? 'tw-bg-accent-tint tw-text-accent hover:tw-bg-surface-hover'
                : 'tw-bg-surface-sunken tw-text-ink-secondary hover:tw-bg-surface-hover'
            }`}
            title={isLocked ? 'Unlock width' : 'Lock width'}
            aria-label={isLocked ? 'Unlock width' : 'Lock width'}
          >
            <span className="icon-follow-ink">{isLocked ? <LockedIcon /> : <UnlockedIcon />}</span>
          </button>
        </div>

        {/* Width Slider */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-xs tw-text-ink-secondary tw-w-6">{minWidth}</span>
          <div className="tw-flex-1">
            {/*
              `hideLabel`: the "Width" `FieldTitle` above this row is the visible
              label and stays. What the slider gains is the NAME — it had none at
              all, so it announced as "slider, 40" with the surrounding text
              unreachable. Same string as the FieldTitle, so the two cannot drift.
            */}
            <Slider
              label="Width"
              hideLabel
              size="sm"
              formatValue={(percentage) => `${percentage}%`}
              data-testid="configpanel-containerconfigpanel-width-slider"
              value={Math.round(localWidth)}
              onChange={handleSliderChange}
              disabled={isLocked}
              min={minWidth}
              max={maxWidth}
            />
          </div>
          <span className="tw-text-xs tw-text-ink-secondary tw-w-6">{maxWidth}</span>
        </div>

        {/* Reset Row Button */}
        {containersInRow.length > 0 && (
          <button data-testid="configpanel-containerconfigpanel-button-5"
            onClick={resetRowWidths}
            className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-accent hover:tw-text-accent-hover tw-transition-colors"
          >
            <ResetIcon />
            <span>Reset row widths</span>
          </button>
        )}

        {/* Width Distribution Info */}
        {containersInRow.length > 0 && (
          <div className="tw-text-xs tw-text-ink-secondary tw-bg-surface-sunken tw-p-2 tw-rounded">
            <span className="tw-font-medium">Row distribution:</span>{' '}
            {containersInRow.length + 1} containers
            {containersInRow.some((c) => c.configuration?.widthLocked) && (
              <span className="tw-ml-1">
                ({containersInRow.filter((c) => c.configuration?.widthLocked).length} locked)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Height Field (Display Only) */}
      <TextField
        label="Height"
        labelSize="sm"
        value="Dynamic"
        disabled
        onChange={() => {}}
      />

      {/* Container Alignment */}
      {/*
      <div className="tw-space-y-2">
        <FieldTitle label="Alignment" size="sm" />
        <DeltaSwitch
          id="container-alignment-switch"
          labels={['Left', 'Centre', 'Right']}
          defaultSelectedIndex={
            container.configuration?.alignment === 'left' ? 0 :
            container.configuration?.alignment === 'center' ? 1 :
            container.configuration?.alignment === 'right' ? 2 : 0
          }
          onSelectionChange={(value) => {
            let alignment: string;
            if (value === 'Left') alignment = 'left';
            else if (value === 'Centre') alignment = 'center';
            else if (value === 'Right') alignment = 'right';
            else alignment = 'left';
            handleConfigUpdate('alignment', alignment);
          }}
        />
      </div>
      */}

      {/* Layout Dropdown */}
      <Dropdown
        label="Layout"
        labelSize="sm"
        value={container.configuration?.layout || 'vertical'}
        onChange={(value) => handleConfigUpdate('layout', value)}
        options={LAYOUT_OPTIONS}
      />

      <Divider />

      {/* Checkbox Options */}
      <div className="tw-space-y-3">
        <Checkbox
          label="Show Title"
          checked={container.configuration?.showTitle || false}
          onChange={(checked) => handleConfigUpdate('showTitle', checked)}
        />

        <Checkbox
          label="Collapsible"
          checked={container.configuration?.collapsible || false}
          onChange={(checked) => {
            if (checked) {
              // Set both in one call to avoid stale-closure overwrite
              updateContainer(container.id, {
                configuration: {
                  ...container.configuration,
                  collapsible: true,
                  showTitle: true,
                },
              });
            } else {
              updateContainer(container.id, {
                configuration: {
                  ...container.configuration,
                  collapsible: false,
                  collapseByDefault: false,
                },
              });
            }
          }}
        />

        {container.configuration?.collapsible && (
          <div className="tw-ml-6">
            <Checkbox
              label="Collapse by Default"
              checked={container.configuration?.collapseByDefault || false}
              onChange={(checked) => handleConfigUpdate('collapseByDefault', checked)}
            />
          </div>
        )}

        <Checkbox
          label="Behave as Filter"
          checked={container.configuration?.behaveAsFilter || false}
          onChange={(checked) => handleConfigUpdate('behaveAsFilter', checked)}
        />
      </div>

      <div className="tw-space-y-2">
          <Divider />
          {/* Show Border */}
        <Checkbox
          label="Show Border"
          checked={container.configuration?.showBorder || false}
          onChange={(checked) => {
            const borderUpdate: Record<string, unknown> = { showBorder: checked };
            if (checked) {
              const currentBorder = container.configuration?.border;
              if (!currentBorder || currentBorder.length === 0) {
                borderUpdate.border = ['all'];
              }
            } else {
              borderUpdate.border = [];
            }
            updateContainer(container.id, {
              configuration: {
                ...container.configuration,
                ...borderUpdate,
              },
            });
          }}
        />

        {/* Set Border - expanded when showBorder is true */}
        {container.configuration?.showBorder && (
          <BorderPlacementSelector
            value={container.configuration?.border || []}
            onChange={(border) => handleConfigUpdate('border', border)}
          />
        )}
      </div>

      {/* Submodel Info & Container Contents */}
      <div className="tw-space-y-2">
        {lockedSubmodelId && (
          <>
            <Divider />
            <div className="tw-p-3 tw-bg-surface-sunken tw-rounded">
              <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
                <span className="tw-text-ink-secondary tw-flex tw-items-center icon-follow-ink"><LockedIcon /></span>
                <Typography variant="caption-metadata" className="tw-text-ink-secondary">
                  Locked to submodel
                </Typography>
              </div>
              <Typography variant="title" className="tw-text-ink">
                {lockedSubmodelName}
              </Typography>
              {lockedSubmodelMax > 1 && (
                <Typography variant="caption-metadata" className="tw-text-accent tw-mt-1 tw-block">
                  Multiple instances (max {lockedSubmodelMax}) — this container and everything nested inside it accept only this submodel.
                </Typography>
              )}
            </div>
          </>
        )}
        <ContainerContentsList container={container} />
      </div>
    </div>
  );
};

export default ContainerConfigPanel;
